import React from 'react'
import { Link } from 'react-router-dom'
import { Headphones, Mail, MessageCircle, Clock } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const SupportPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <Headphones className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              We're Here to Help
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Get the support you need to complete your 1031 exchange successfully.
            </p>
          </div>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Email Support */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 mx-auto">
                <Mail className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Email Support
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Send us an email and we'll respond within 24 hours.
              </p>
              <a href="mailto:support@open1031.ai" className="text-[#69adba] hover:text-[#5a9aa6] font-medium">
                support@open1031.ai
              </a>
            </div>

            {/* Live Chat */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 mx-auto">
                <MessageCircle className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Live Chat
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Chat with our support team in real-time during business hours.
              </p>
              <Link to="/">
                <Button className="bg-[#69adba] hover:bg-[#5a9aa6] text-white">
                  Start Chat
                </Button>
              </Link>
            </div>

            {/* Phone Support */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 text-center">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4 mx-auto">
                <Headphones className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Phone Support
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Call us Monday-Friday, 9am-5pm PST for immediate assistance.
              </p>
              <a href="tel:1-800-OPEN-1031" className="text-[#69adba] hover:text-[#5a9aa6] font-medium">
                1-800-OPEN-1031
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support Hours */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800/50 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center mb-6">
                <Clock className="h-6 w-6 text-[#69adba] mr-3" />
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Support Hours
                </h2>
              </div>
              <div className="space-y-3 text-slate-700 dark:text-slate-300">
                <div className="flex justify-between">
                  <span className="font-medium">Monday - Friday</span>
                  <span>9:00 AM - 5:00 PM PST</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Saturday</span>
                  <span>10:00 AM - 2:00 PM PST</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Sunday</span>
                  <span>Closed</span>
                </div>
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    For urgent matters outside business hours, please email us and we'll respond as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 text-center mb-12">
              Quick Links
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link to="/faq" className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Frequently Asked Questions
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Find answers to common questions about our platform and services.
                </p>
              </Link>
              <Link to="/documentation" className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Documentation
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Comprehensive guides and tutorials to help you get started.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

